import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExhibitionNavBar from './components/ExhibitionNavBar';
import BoothGallery from './components/BoothGallery';
import FeaturedInstitutesSection from './components/FeaturedInstitutesSection';
import SearchFilterBar from './components/SearchFilterBar';
import InfoDeskModal from './components/InfoDeskModal';
import SearchCoursesModal from './components/SearchCoursesModal';
import { instituteService } from '../../services/instituteService';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';

const PAGE_SIZE = 24;

// Auth gate modal for unauthenticated interactions
const AuthGateModal = ({ onClose, onRegister, onLogin }) => (
  <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center" onClick={(e) => e?.stopPropagation()}>
      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
        <Icon name="Lock" size={28} color="white" />
      </div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Register to View Details</h2>
      <p className="text-muted-foreground mb-6">
        Create a free account to access institute details, chat with counsellors, book sessions, and more.
      </p>
      <div className="space-y-3">
        <button
          onClick={onRegister}
          className="w-full py-3 px-6 bg-gradient-to-r from-cyan-600 to-violet-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          Register Free — It's Quick!
        </button>
        <button
          onClick={onLogin}
          className="w-full py-3 px-6 border-2 border-violet-200 text-violet-700 font-semibold rounded-xl hover:bg-violet-50 transition-all"
        >
          Already have an account? Login
        </button>
        <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Continue Browsing
        </button>
      </div>
    </div>
  </div>
);

const ExhibitionHall = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [showInfoDesk, setShowInfoDesk] = useState(false);
  const [showSearchCourses, setShowSearchCourses] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);

  // Approval state
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [checkingApproval, setCheckingApproval] = useState(true);

  // Featured institutes
  const [featuredInstitutes, setFeaturedInstitutes] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Search/filter/pagination state
  const [filters, setFilters] = useState({ search: '', state: '', category: '', feesRange: '', ranking: '', page: 1 });
  const [institutes, setInstitutes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check approval status on mount
  useEffect(() => {
    checkApprovalStatus();
  }, [user]);

  const checkApprovalStatus = async () => {
    try {
      if (!user?.id) {
        setCheckingApproval(false);
        return;
      }
      const { data, error } = await supabase?.from('student_profiles')?.select('approval_status')?.eq('user_id', user?.id)?.maybeSingle();
      if (error) throw error;
      if (!data) {
        setCheckingApproval(false);
        return;
      }
      const status = data?.approval_status || 'pending';
      setApprovalStatus(status);
      if (status !== 'approved') {
        navigate('/pending-approval');
        return;
      }
    } catch (error) {
      console.error('Error checking approval status:', error);
    } finally {
      setCheckingApproval(false);
    }
  };

  // Load featured institutes — always load (public)
  useEffect(() => {
    fetchFeaturedInstitutes();
  }, []);

  // Load institutes when filters change — always load (public)
  useEffect(() => {
    fetchInstitutes();
  }, [filters]);

  const fetchFeaturedInstitutes = async () => {
    try {
      setLoadingFeatured(true);
      const data = await instituteService?.getFeaturedInstitutes();
      const transformed = data?.map(i => ({
        id: i?.id,
        name: i?.instituteName,
        instituteName: i?.instituteName,
        logo: i?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png',
        logoUrl: i?.logoUrl,
        banner: i?.bannerUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1f477cd97-1768735565694.png',
        bannerUrl: i?.bannerUrl,
        categories: i?.categories || [],
        ranking: i?.ranking,
        state: i?.state,
        boothType: i?.boothType || 'free'
      }));
      setFeaturedInstitutes(transformed || []);
    } catch (err) {
      console.error('Error fetching featured institutes:', err);
    } finally {
      setLoadingFeatured(false);
    }
  };

  const fetchInstitutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Parse fees range
      let feesMin = null;
      let feesMax = null;
      if (filters?.feesRange) {
        const parts = filters?.feesRange?.split('-');
        feesMin = parts?.[0] ? Number(parts?.[0]) : null;
        feesMax = parts?.[1] ? Number(parts?.[1]) : null;
      }

      const result = await instituteService?.getExhibitionHallInstitutes({
        search: filters?.search,
        state: filters?.state,
        category: filters?.category,
        feesMin,
        feesMax,
        ranking: filters?.ranking,
        page: filters?.page || 1,
        pageSize: PAGE_SIZE
      });

      const transformed = result?.data?.map(i => ({
        id: i?.id,
        name: i?.instituteName || 'Unknown Institute',
        logo: i?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png',
        logoAlt: `${i?.instituteName || 'Institute'} logo`,
        banner: i?.bannerUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1f477cd97-1768735565694.png',
        bannerAlt: `${i?.instituteName || 'Institute'} campus view`,
        category: i?.courses?.[0]?.category || 'General',
        categories: i?.categories || [],
        boothType: i?.boothType || 'free',
        liveNow: false,
        visitors: i?.virtualBooths?.[0]?.visit_count || 0,
        ranking: i?.ranking,
        state: i?.state
      })) || [];

      setInstitutes(transformed);
      setTotalCount(result?.total || 0);
    } catch (err) {
      console.error('Error fetching institutes:', err);
      setError(err?.message || 'Failed to load institutes');
      toast?.error('Failed to load institutes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: document.getElementById('results-section')?.offsetTop - 80 || 0, behavior: 'smooth' });
  };

  const handleBoothClick = (instituteId) => {
    if (!user) {
      setShowAuthGate(true);
      return;
    }
    navigate(`/virtual-booth/${instituteId}`);
  };

  const handleNavAction = (action) => {
    switch (action) {
      case 'chat': navigate('/chat-interface'); break;
      case 'info-desk': setShowInfoDesk(true); break;
      case 'explore-booths':
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'search-courses': setShowSearchCourses(true); break;
      case 'talk-to-universities': navigate('/explore-institutions'); break;
      case 'win-prizes': toast?.info('Win Prizes feature coming soon!'); break;
      default: break;
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const currentPage = filters?.page || 1;

  // Show loading while checking approval (only for logged-in users)
  if (checkingApproval && user?.id) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Icon name="Loader" className="w-12 h-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (user?.id && approvalStatus !== 'approved' && approvalStatus !== null) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200">
      <ExhibitionNavBar onAction={handleNavAction} />
      <div className="pt-16">
        {/* Hero Banner */}
        <div className="relative w-full h-[500px] bg-blue-200 overflow-hidden">
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-white rounded-xl shadow-xl p-4 text-center border-2 border-primary">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Icon name="Building2" size={24} color="var(--color-primary)" />
                <h1 className="text-2xl font-heading font-bold text-primary">VIRTUAL EDUCATION FAIR 2026</h1>
              </div>
              <p className="text-sm text-muted-foreground">Explore World-Class Universities from Anywhere</p>
            </div>
          </div>

          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/4 z-20">
            <div className="relative">
              <div
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-10 py-5 rounded-xl shadow-2xl border-4 border-white transform hover:scale-105 transition-transform cursor-pointer"
                onClick={() => document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <div className="flex items-center gap-3">
                  <Icon name="DoorOpen" size={32} color="white" />
                  <div>
                    <h2 className="text-3xl font-heading font-bold tracking-wide">HALL ENTRANCE</h2>
                    <p className="text-base mt-1 opacity-90">Click to Enter the Virtual Fair</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-yellow-900 px-6 py-2 rounded-full text-sm font-semibold shadow-lg animate-pulse">
                {loading ? 'Loading...' : error ? 'Error' : `${totalCount} Booths Available`}
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-200 to-transparent"></div>
          <div className="absolute bottom-8 left-8 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 cursor-pointer hover:bg-red-700 transition-colors"
            onClick={() => setShowInfoDesk(true)}>
            <Icon name="Info" size={24} color="white" />
            <span className="font-semibold text-lg">Info Desk</span>
          </div>
          <div className="absolute bottom-8 right-8 bg-purple-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 cursor-pointer hover:bg-purple-700 transition-colors"
            onClick={() => toast?.info('Get Started with your virtual fair journey!')}>
            <Icon name="Sparkles" size={24} color="white" />
            <span className="font-semibold text-lg">Get Started Here!</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-[1440px] mx-auto px-6 py-12">

          {/* ── Featured Institutes ── */}
          {!loadingFeatured && featuredInstitutes?.length > 0 && (
            <FeaturedInstitutesSection
              institutes={featuredInstitutes}
              onBoothClick={handleBoothClick}
            />
          )}

          {/* ── Results Section ── */}
          <div id="results-section">
            <div className="mb-6">
              <h2 className="text-3xl font-heading font-bold text-foreground mb-1">Exhibition Booths</h2>
              <p className="text-muted-foreground">Search, filter, and explore institutes — then click any booth to connect</p>
            </div>

            {/* Search + Filters */}
            <SearchFilterBar
              filters={filters}
              onChange={handleFiltersChange}
              totalResults={totalCount}
              loading={loading}
            />

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading exhibition booths...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-3" />
                <p className="text-red-800 font-semibold mb-2">Error Loading Booths</p>
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={fetchInstitutes} iconName="RefreshCw" iconPosition="left">Try Again</Button>
              </div>
            )}

            {/* Booth Gallery */}
            {!loading && !error && institutes?.length > 0 && (
              <BoothGallery
                institutes={institutes}
                onBoothClick={handleBoothClick}
                selectedCategory={filters?.category || ''}
              />
            )}

            {/* Empty State */}
            {!loading && !error && institutes?.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Icon name="Building2" size={64} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-foreground mb-2">No Institutes Found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search or filters to find more institutes.
                </p>
                <Button
                  onClick={() => setFilters({ search: '', state: '', category: '', feesRange: '', ranking: '', page: 1 })}
                  variant="outline"
                  iconName="X"
                  iconPosition="left"
                >
                  Clear All Filters
                </Button>
              </div>
            )}

            {/* Pagination */}
            {!loading && !error && totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="ChevronLeft" size={16} />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    ?.filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    ?.reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr?.[idx - 1] > 1) acc?.push('...');
                      acc?.push(p);
                      return acc;
                    }, [])
                    ?.map((item, idx) =>
                      item === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-2 text-muted-foreground text-sm">…</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => handlePageChange(item)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === item
                              ? 'bg-primary text-white shadow-sm'
                              : 'border border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )
                  }
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            )}

            {/* Page info */}
            {!loading && !error && totalCount > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} institutes
              </p>
            )}
          </div>

          {/* Why Visit section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-2xl font-bold text-foreground mb-4">Why Student Engagement Platform</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                    <span>Connect with Top universities from around the world</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                    <span>Live video counseling with admissions officers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                    <span>Explore scholarship opportunities and financial aid</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                    <span>Download brochures and application materials instantly</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <div
                className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => toast?.info('Quick Apply feature coming soon! Visit individual booths to connect with institutes.')}
              >
                <Icon name="Zap" size={32} className="mb-3" />
                <h4 className="text-xl font-bold mb-2">Quick Apply</h4>
                <p className="text-sm mb-4 opacity-90">Apply to multiple universities with one form</p>
                <div className="flex items-center gap-2 font-semibold">
                  <span>Start Application</span>
                  <Icon name="ArrowRight" size={18} />
                </div>
              </div>
              <div
                className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => toast?.info('Financial Aid calculator coming soon!')}
              >
                <Icon name="DollarSign" size={32} className="mb-3" />
                <h4 className="text-xl font-bold mb-2">Financial Aid</h4>
                <p className="text-sm mb-4 opacity-90">Calculate your eligibility for scholarships</p>
                <div className="flex items-center gap-2 font-semibold">
                  <span>Check Eligibility</span>
                  <Icon name="ArrowRight" size={18} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showInfoDesk && <InfoDeskModal onClose={() => setShowInfoDesk(false)} />}
      {showSearchCourses && <SearchCoursesModal onClose={() => setShowSearchCourses(false)} user={user} />}
      {showAuthGate && (
        <AuthGateModal
          onClose={() => setShowAuthGate(false)}
          onRegister={() => navigate('/student-registration')}
          onLogin={() => navigate('/student-registration')}
        />
      )}
    </div>
  );
};

export default ExhibitionHall;